import React, { useContext, useState, useEffect, Component } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import {
  LayoutDashboard, Users, Calendar, FileText, UsersRound,
  BarChart3, Settings, LogOut, Menu, Bell, ChevronRight, MessageSquare, Phone, Wallet, Bot, Wrench, Plus, BellRing, BellOff, Shield, Clock, X, UserPlus, Hammer, Receipt, Send, Package, Home, Briefcase, LayoutGrid, CheckCircle2, XCircle, BookOpen, HelpCircle, Megaphone, MapPinned
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { filterNavItems, canAccessPage, getRoleDisplayName, isDispatcher } from "../utils/permissions";
import ThemeToggle from "../components/ThemeToggle";
import { useTheme } from "../contexts/ThemeContext";
import BalanceWidget from "../components/BalanceWidget";
import GlobalSearch from "../components/common/GlobalSearch";
import TapToPayBoot from "../components/payments/TapToPayAwareness";
import LowBalanceBanner from "../components/LowBalanceBanner";
import { useIsMobileApp, useIsIOSApp } from "../hooks/useIsMobileApp";
import PlanSelectionModal from "../components/PlanSelectionModal";
import { useTimezone } from "../contexts/TimezoneContext";
import { playNotificationSound, playSmsTone, syncSoundPrefs, listenForPushMessages } from "../utils/notificationSound";
import OnboardingWizard from "../components/onboarding/OnboardingWizard";
import SetupGuideWidget from "../components/onboarding/SetupGuideWidget";

class PageErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("Page crash:", error, info?.componentStack); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4 p-8">
          <p className="text-red-500 font-semibold text-lg">Something went wrong</p>
          <p className="text-muted-foreground text-sm max-w-md text-center">{this.state.error?.message}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Reload Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const DashboardLayout = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const isMobileApp = useIsMobileApp();
  const isIOSAppShell = useIsIOSApp();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [appNotifications, setAppNotifications] = useState([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const { isDark } = useTheme();
  const { getTimezoneLabel } = useTimezone();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [isAccountFrozen, setIsAccountFrozen] = useState(false);
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);
  // Guided onboarding — status fetched for owners; wizard auto-launches for
  // new accounts once the subscription/trial popup (Step 0) is out of the way.
  const [onbStatus, setOnbStatus] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [onbAutoLaunched, setOnbAutoLaunched] = useState(false);

  const isOwnerAccount = user && !user.parent_user_id && (user.role === "owner" || !user.role);

  const fetchOnboardingStatus = React.useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } });
      setOnbStatus(res.data);
      return res.data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    // Refetch when the plan modal closes — trial/subscription activation
    // changes should_launch, and the wizard must only appear after Step 0.
    if (isOwnerAccount && !showPlanModal) fetchOnboardingStatus();
  }, [isOwnerAccount, showPlanModal, fetchOnboardingStatus]);

  // Auto-launch for NEW accounts: only after the plan/trial modal is closed
  // (never modal-on-modal) and only once per session.
  useEffect(() => {
    if (!onbStatus || showPlanModal || wizardOpen || onbAutoLaunched) return;
    if (onbStatus.should_launch) {
      setOnbAutoLaunched(true);
      setWizardOpen(true);
    }
  }, [onbStatus, showPlanModal, wizardOpen, onbAutoLaunched]);

  // Allow any page (widget, settings) to open the Setup Guide.
  useEffect(() => {
    const openGuide = () => { fetchOnboardingStatus(); setWizardOpen(true); };
    window.addEventListener("fieldz-open-setup", openGuide);
    return () => window.removeEventListener("fieldz-open-setup", openGuide);
  }, [fetchOnboardingStatus]);

  // Deep link from the setup reminder email: /dashboard?setup=open
  useEffect(() => {
    if (!isOwnerAccount) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("setup") === "open") {
      fetchOnboardingStatus();
      setWizardOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [isOwnerAccount, fetchOnboardingStatus]);

  // Show plan selection modal for inactive subscriptions — user cannot dismiss until resolved
  useEffect(() => {
    if (!user) return;

    // Store pending payment link if admin sent one
    if (user.pending_payment) {
      setPendingPayment(user.pending_payment);
    } else {
      // No pending payment from backend — clear any stale local state
      setPendingPayment(null);
    }

    const inactiveStatuses = ["frozen", "expired", "cancelled", "canceled", "past_due", "suspended", "inactive", "unpaid"];
    const status = user.subscription_status || "";
    const accountStatus = user.account_status || "";

    // Team members (dispatchers, admins with parent_user_id) never see billing prompts
    if (user.parent_user_id) {
      setIsAccountFrozen(false);
      setIsSubscriptionExpired(false);
      setShowPlanModal(false);
      return;
    }

    // If user has a paid plan and status is NOT one of the problem states, clear all alerts
    const hasPaidPlan = user.subscription_plan && user.subscription_plan !== "starter";
    const statusIsHealthy = hasPaidPlan && !inactiveStatuses.includes(status) && !inactiveStatuses.includes(accountStatus);

    if (statusIsHealthy) {
      setIsAccountFrozen(false);
      setIsSubscriptionExpired(false);
      setShowPlanModal(false);
      return;
    }

    if (accountStatus === "frozen" || status === "frozen") {
      setIsAccountFrozen(true);
      setIsSubscriptionExpired(false);
      setShowPlanModal(true);
    } else if (inactiveStatuses.includes(status) || inactiveStatuses.includes(accountStatus)) {
      setIsSubscriptionExpired(true);
      setIsAccountFrozen(false);
      setShowPlanModal(true);
    } else if (
      user.subscription_plan === "starter" &&
      !user.trial_activated &&
      status !== "active"
    ) {
      setIsAccountFrozen(false);
      setIsSubscriptionExpired(false);
      setShowPlanModal(true);
    }
  }, [user]);

  // Fetch unread message count
  useEffect(() => {
    // Expose navigate function for native mobile app bridge
    window.__navigateTo = (path) => {
      navigate(path);
    };
    return () => { delete window.__navigateTo; };
  }, [navigate]);

  const markAllNotifsRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/notifications/mark-read`, { mark_all: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadNotifCount(0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem("token");
        const config = {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        };
        const [msgRes, notifRes] = await Promise.all([
          axios.get(`${API}/sms/unread-count`, config).catch(() => ({ data: { unread_count: 0 } })),
          axios.get(`${API}/notifications`, config).catch(() => ({ data: { notifications: [], unread_count: 0 } })),
        ]);
        setUnreadMessages(msgRes.data?.unread_count || 0);
        setAppNotifications((notifRes.data?.notifications || []).slice(0, 10));
        setUnreadNotifCount(notifRes.data?.unread_count || 0);
      } catch (error) {
        // Silently fail - not critical
      }
    };
    fetchUnreadCount();
    // Refresh every 15 seconds for timely notifications
    const interval = setInterval(fetchUnreadCount, 15000);
    // Instant refresh when a conversation is opened/marked read
    window.addEventListener("fieldz-unread-refresh", fetchUnreadCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener("fieldz-unread-refresh", fetchUnreadCount);
    };
  }, []);

  // Pull the user's saved sound preferences to this device (per-user tones)
  useEffect(() => {
    syncSoundPrefs(API);
  }, []);

  // Listen for push notifications while app is in foreground.
  // Two sources feed the same handler: (1) service-worker PUSH_RECEIVED
  // messages, (2) OneSignal foregroundWillDisplay via the
  // 'fieldz-push-foreground' custom event. Deduped by message SID /
  // notification id so one SMS never double-plays or double-toasts.
  useEffect(() => {
    const seen = new Set();
    const handleNotif = (notification) => {
      if (!notification) return;
      const key = notification.data?.message_sid || notification.id || `${notification.title}|${notification.body}`;
      if (key) {
        if (seen.has(key)) return;
        seen.add(key);
        setTimeout(() => seen.delete(key), 15000);
      }
      // Play sound (respects the user's sound preference internally)
      const notifType = notification.data?.type;
      if (notifType === 'new_message') {
        playSmsTone();
      } else {
        playNotificationSound();
      }
      // Show in-app toast with action
      const url = notification.url;
      if (notifType === 'new_message') {
        toast.info(notification.title, {
          description: notification.body,
          action: url ? { label: 'Open', onClick: () => navigate(url) } : undefined,
          duration: 6000,
        });
        // Bump unread count immediately
        setUnreadMessages(prev => prev + 1);
      } else {
        toast(notification.title, {
          description: notification.body,
          action: url ? { label: 'View', onClick: () => navigate(url) } : undefined,
          duration: 5000,
        });
        setUnreadNotifCount(prev => prev + 1);
      }
    };
    const cleanup = listenForPushMessages(handleNotif);
    const fgHandler = (e) => handleNotif(e.detail);
    window.addEventListener('fieldz-push-foreground', fgHandler);
    return () => {
      cleanup();
      window.removeEventListener('fieldz-push-foreground', fgHandler);
    };
  }, [navigate]);

  // All available navigation items
  const allNavItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/dashboard/customers", icon: Users, label: "Customers" },
    { path: "/dashboard/jobs", icon: Calendar, label: "Jobs" },
    { path: "/dashboard/invoices", icon: FileText, label: "Invoices" },
    { path: "/dashboard/team", icon: UsersRound, label: "Team" },
    { path: "/dashboard/technicians", icon: Wrench, label: "Technicians" },
    { path: "/dashboard/team-map", icon: MapPinned, label: "Team Map" },
    { path: "/dashboard/messages", icon: MessageSquare, label: "Messages" },
    { path: "/dashboard/calls", icon: Phone, label: "Call History" },
    { path: "/dashboard/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/dashboard/inventory", icon: Package, label: "Inventory" },
    { path: "/dashboard/financials", icon: Wallet, label: "Financials" },
    { path: "/dashboard/expenses", icon: Receipt, label: "Expenses" },
    { path: "/dashboard/automations", icon: Bot, label: "Fieldz AI", highlight: true },
    { path: "/dashboard/campaigns", icon: Megaphone, label: "Campaigns" },
    { path: "/dashboard/settings", icon: Settings, label: "Settings" },
  ];

  // Filter navigation items based on user role
  const navItems = filterNavItems(user, allNavItems);

  // Quick actions for mobile bottom nav
  const mobileQuickActions = [
    { path: "/dashboard/financials", icon: Wallet, label: "Finance" },
    { path: "/dashboard/messages", icon: MessageSquare, label: "Messages" },
    { path: "/dashboard/jobs", icon: Calendar, label: "Jobs" },
  ];

  // Grid items for the mobile "More" menu (matches native app layout)
  const mobileMenuGrid = [
    { path: "/dashboard", icon: Home, label: "Dashboard", color: "text-amber-400", bg: "bg-amber-400/15" },
    { path: "/dashboard/customers", icon: Users, label: "Customers", color: "text-blue-400", bg: "bg-blue-400/15" },
    { path: "/dashboard/jobs", icon: Briefcase, label: "Jobs", color: "text-orange-400", bg: "bg-orange-400/15" },
    { path: "/dashboard/invoices", icon: FileText, label: "Invoices", color: "text-emerald-400", bg: "bg-emerald-400/15" },
    { path: "/dashboard/technicians", icon: Wrench, label: "Technicians", color: "text-purple-400", bg: "bg-purple-400/15" },
    { path: "/dashboard/team-map", icon: MapPinned, label: "Team Map", color: "text-rose-400", bg: "bg-rose-400/15" },
    { path: "/dashboard/team", icon: UsersRound, label: "Team", color: "text-cyan-400", bg: "bg-cyan-400/15" },
    { path: "/dashboard/messages", icon: MessageSquare, label: "Messages", color: "text-green-400", bg: "bg-green-400/15" },
    { path: "/dashboard/calls", icon: Phone, label: "Call History", color: "text-violet-400", bg: "bg-violet-400/15" },
    { path: "/dashboard/analytics", icon: BarChart3, label: "Analytics", color: "text-pink-400", bg: "bg-pink-400/15" },
    { path: "/dashboard/inventory", icon: Package, label: "Inventory", color: "text-orange-400", bg: "bg-orange-400/15" },
    { path: "/dashboard/financials", icon: Wallet, label: "Financials", color: "text-teal-400", bg: "bg-teal-400/15" },
    { path: "/dashboard/expenses", icon: Receipt, label: "Expenses", color: "text-rose-400", bg: "bg-rose-400/15" },
    { path: "/dashboard/automations", icon: Bot, label: "Fieldz AI", color: "text-yellow-400", bg: "bg-yellow-400/15", highlight: true },
    { path: "/dashboard/campaigns", icon: Megaphone, label: "Campaigns", color: "text-amber-400", bg: "bg-amber-400/15" },
    { path: "/dashboard/settings", icon: Settings, label: "Settings", color: "text-gray-400", bg: "bg-gray-400/15" },
  ];

  // State for showing create job dialog
  const [showCreateJob, setShowCreateJob] = useState(false);

  const handleLogout = async () => {
    // Logging out while impersonating = exit support mode (restores the admin)
    try {
      const { getImpersonationState, exitImpersonation } = await import("../utils/impersonation");
      if (getImpersonationState().active) {
        await exitImpersonation();
        return;
      }
    } catch (e) {}
    try {
      await axios.post(`${API}/auth/logout`, {}, {});
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Unlink this browser from the user's push identity BEFORE clearing auth
    try {
      const { oneSignalSafeLogout } = await import("../contexts/PushNotificationContext");
      await oneSignalSafeLogout();
    } catch (e) {}
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // If running inside native app, use native logout to return to native LoginScreen
    if (window.FieldzNative?.isNativeApp && window.FieldzNative?.logout) {
      window.FieldzNative.logout();
      return;
    }
    toast.success("Logged out successfully");
    navigate("/");
  };

  const isActive = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`p-6 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
        <Link to="/dashboard" className="flex items-center justify-center">
          <img src={isDark ? "/logo.png" : "/logo-dark.png"} alt="Fieldz AI" className="h-12 object-contain" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative ${
              isActive(item.path)
                ? "bg-[#FFCC00]/10 text-[#FFCC00] border border-[#FFCC00]/20"
                : item.highlight 
                  ? "text-[#FFCC00]/80 hover:text-[#FFCC00] hover:bg-[#FFCC00]/5 border border-[#FFCC00]/10"
                  : isDark 
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon className={`w-5 h-5 ${isActive(item.path) ? "text-[#FFCC00]" : item.highlight ? "text-[#FFCC00]/80" : isDark ? "group-hover:text-white" : "group-hover:text-gray-900"}`} />
            <span className="font-medium">{item.label}</span>
            {item.highlight && !isActive(item.path) && (
              <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-[#FFCC00]/20 text-[#FFCC00] rounded-full">AI</span>
            )}
            {/* Unread messages badge for Messages nav item */}
            {item.path === "/dashboard/messages" && unreadMessages > 0 && (
              <span className="ml-auto flex items-center justify-center min-w-5 h-5 px-1.5 bg-[#FFCC00] text-black text-xs font-bold rounded-full">
                {unreadMessages > 99 ? '99+' : unreadMessages}
              </span>
            )}
            {isActive(item.path) && item.path !== "/dashboard/messages" && <ChevronRight className="w-4 h-4 ml-auto" />}
          </Link>
        ))}

        {/* Admin Dashboard link - only for admins */}
        {user?.is_admin && (
          <Link
            to="/admin"
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group mt-2 border ${
              isDark
                ? "text-red-400 bg-red-500/10 border-red-500/20 hover:bg-red-500/20"
                : "text-red-600 bg-red-50 border-red-200 hover:bg-red-100"
            }`}
            data-testid="nav-admin-dashboard"
          >
            <Shield className="w-5 h-5" />
            <span className="font-medium">Admin Dashboard</span>
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Link>
        )}
      </nav>

      {/* User Section */}
      <div className={`p-4 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
          <Avatar className="h-10 w-10 border-2 border-[#FFCC00]/30">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-[#0046BE] text-white">
              {user?.name?.charAt(0) || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.name}</p>
            <p className={`text-xs truncate capitalize ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{user?.subscription_plan} Plan</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#020817] dark' : 'bg-gray-50 light'}`}>
      {/* Plan Selection Modal for new users / frozen accounts / expired subscriptions.
          Not rendered in the iOS app (Apple Guideline 3.1.1 — no in-app
          subscription purchase UI); frozen/expired accounts get a neutral
          notice there instead. Android and web unchanged. */}
      {showPlanModal && !isIOSAppShell && (
        <PlanSelectionModal
          onClose={() => { if (!isAccountFrozen && !isSubscriptionExpired) setShowPlanModal(false); }}
          isFrozen={isAccountFrozen}
          isExpired={isSubscriptionExpired}
        />
      )}
      {showPlanModal && isIOSAppShell && (isAccountFrozen || isSubscriptionExpired) && (
        <div
          className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="ios-subscription-notice"
        >
          <div className="max-w-sm w-full text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#FFCC00]/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#FFCC00]" />
            </div>
            <h2 className="text-xl font-bold text-foreground">Subscription inactive</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your Fieldz AI subscription is not active. Subscriptions cannot be purchased or managed in this app.
            </p>
            <Button onClick={handleLogout} variant="outline" data-testid="ios-subscription-signout">
              Sign out
            </Button>
          </div>
        </div>
      )}

      {/* Sidebar - Desktop */}
      <aside className={`fixed left-0 top-0 bottom-0 w-64 hidden lg:block transition-colors duration-300 ${
        isDark ? 'glass-sidebar' : 'bg-white border-r border-gray-200'
      }`} data-testid="sidebar">
        <NavContent />
      </aside>

      {/* Mobile Bottom Sheet Menu (matches native app "More" menu) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-3xl overflow-hidden"
            style={{ background: isDark ? "rgba(10,10,18,0.98)" : "rgba(255,255,255,0.98)", borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)" }}
            data-testid="mobile-menu-sheet"
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
            </div>
            <h2 className={`text-lg font-bold px-5 pb-4 font-['Manrope'] ${isDark ? 'text-white' : 'text-gray-900'}`}>Menu</h2>

            {/* 4-column icon grid */}
            <div className="grid grid-cols-4 gap-y-4 gap-x-2 px-4 pb-4">
              {mobileMenuGrid.map((item) => (
                <Link
                  key={item.path + item.label}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className="flex flex-col items-center gap-1.5"
                  data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${item.bg}`}>
                    <item.icon className={`w-6 h-6 ${item.color}`} />
                  </div>
                  <span className={`text-[11px] font-medium text-center leading-tight ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{item.label}</span>
                </Link>
              ))}
            </div>

            {/* Sign Out */}
            <div className="px-4 pb-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-red-500/20 bg-red-500/5 text-red-400 font-medium text-sm transition-colors active:bg-red-500/10"
                data-testid="menu-sign-out"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
            <div className={`h-safe-area-inset-bottom ${isDark ? 'bg-[#0a0a12]' : 'bg-white'}`} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className={`h-16 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-50 backdrop-blur-lg transition-colors duration-300 ${
          isDark ? 'bg-[#020817]/50 border-b border-white/5' : 'bg-white/80 border-b border-gray-200'
        }`} data-testid="dashboard-header">
          <div className="flex items-center gap-4">
            {/* Menu button - only visible on desktop, hidden on mobile since we have bottom nav */}
            <div className="hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
                    <Menu className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
                  </Button>
                </SheetTrigger>
              </Sheet>
            </div>
            <div>
              <h1 className={`text-lg font-semibold font-['Manrope'] ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {navItems.find(item => isActive(item.path))?.label || "Dashboard"}
              </h1>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {user?.company_name || "Welcome back!"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Global search — jobs / customers / invoices */}
            <GlobalSearch />

            {/* Timezone Indicator */}
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-theme-surface border border-theme text-xs text-muted-foreground" data-testid="timezone-indicator">
              <Clock className="w-3 h-3" />
              <span>{getTimezoneLabel()}</span>
            </div>

            {/* Theme Toggle - hidden on mobile */}
            <div className="hidden sm:block">
              <ThemeToggle variant="icon" />
            </div>
            
            {/* Admin Panel Button - Only for admins */}
            {user?.is_admin && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/admin')}
                className="hidden sm:flex items-center gap-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
                data-testid="admin-panel-btn"
              >
                <Shield className="w-4 h-4" />
                <span>Admin Panel</span>
              </Button>
            )}
            
            {/* Balance Widget - Hidden in mobile app */}
            {!isMobileApp && <BalanceWidget />}

            {/* Notifications Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className={`relative ${isDark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`} data-testid="notifications-btn">
                  <Bell className="w-5 h-5" />
                  {(unreadMessages + unreadNotifCount) > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-[#FFCC00] rounded-full text-[10px] font-bold text-black flex items-center justify-center">
                      {(unreadMessages + unreadNotifCount) > 9 ? '9+' : (unreadMessages + unreadNotifCount)}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={`w-80 max-h-[400px] overflow-y-auto ${isDark ? 'bg-[#0a0a12] border-white/10' : 'bg-white border-gray-200'}`}>
                <DropdownMenuLabel className={`flex items-center justify-between ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  <span>Notifications</span>
                  {unreadNotifCount > 0 && (
                    <button
                      onClick={(e) => { e.preventDefault(); markAllNotifsRead(); }}
                      className="text-[11px] text-primary hover:underline"
                      data-testid="mark-all-read-btn"
                    >
                      Mark all read
                    </button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className={isDark ? 'bg-white/10' : 'bg-gray-200'} />
                {unreadMessages > 0 && (
                  <DropdownMenuItem 
                    className={`cursor-pointer ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-100'}`}
                    onClick={() => navigate('/dashboard/messages')}
                    data-testid="notif-unread-messages"
                  >
                    <MessageSquare className="w-4 h-4 mr-2 text-[#FFCC00] flex-shrink-0" />
                    <div>
                      <p className="text-sm">New Messages</p>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{unreadMessages} unread message{unreadMessages > 1 ? 's' : ''}</p>
                    </div>
                  </DropdownMenuItem>
                )}
                {appNotifications.map((n) => (
                  <DropdownMenuItem
                    key={n.notification_id}
                    className={`cursor-pointer flex items-start gap-2 py-2 ${!n.read ? (isDark ? 'bg-white/[0.03]' : 'bg-blue-50/50') : ''} ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-100'}`}
                    onClick={() => { if (n.link) navigate(n.link); }}
                    data-testid={`notif-${n.notification_id}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {n.type === 'job_accepted' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> :
                       n.type === 'job_rejected' ? <XCircle className="w-4 h-4 text-red-500" /> :
                       <BellRing className="w-4 h-4 text-[#FFCC00]" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm leading-tight ${!n.read ? 'font-semibold' : ''}`}>{n.title}</p>
                      <p className={`text-xs mt-0.5 line-clamp-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{n.message}</p>
                      <p className={`text-[10px] mt-0.5 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                        {n.created_at ? new Date(n.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#FFCC00] flex-shrink-0 mt-1.5" />}
                  </DropdownMenuItem>
                ))}
                {unreadMessages === 0 && appNotifications.length === 0 && (
                  <div className={`px-3 py-4 text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                    <BellOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No new notifications</p>
                  </div>
                )}
                <DropdownMenuSeparator className={isDark ? 'bg-white/10' : 'bg-gray-200'} />
                <DropdownMenuItem 
                  className={`cursor-pointer ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-100'}`}
                  onClick={() => navigate('/dashboard/settings?tab=notifications')}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Notification Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`flex items-center gap-2 ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`} data-testid="user-menu-btn">
                  <Avatar className={`h-8 w-8 border ${isDark ? 'border-white/20' : 'border-gray-200'}`}>
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#0046BE] text-white text-sm">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={`w-56 ${isDark ? 'bg-[#0a0a12] border-white/10' : 'bg-white border-gray-200'}`}>
                <DropdownMenuLabel className={isDark ? 'text-white/60' : 'text-gray-500'}>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator className={isDark ? 'bg-white/10' : 'bg-gray-200'} />
                <DropdownMenuItem className={`cursor-pointer ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-100'}`} asChild>
                  <Link to="/dashboard/settings" data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                {isOwnerAccount && (
                  <DropdownMenuItem
                    className={`cursor-pointer ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-100'}`}
                    onClick={() => window.dispatchEvent(new Event("fieldz-open-setup"))}
                    data-testid="menu-setup-guide"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Setup Guide{onbStatus && onbStatus.percent < 100 ? ` (${onbStatus.percent}%)` : ""}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className={`cursor-pointer ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-100'}`} asChild>
                  <Link to="/docs" target="_blank" data-testid="menu-docs">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Documentation
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem className={`cursor-pointer ${isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-100'}`} asChild>
                  <Link to="/support" target="_blank" data-testid="menu-support">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Support
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className={isDark ? 'bg-white/10' : 'bg-gray-200'} />
                <DropdownMenuItem 
                  className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                  onClick={handleLogout}
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Low Balance Banner - Hidden in mobile app */}
        {!isMobileApp && <LowBalanceBanner />}

        {/* Pending Payment Link Banner — Admin sent a payment request */}
        {pendingPayment && user && !user.is_admin && (
          <div className={`mx-4 lg:mx-8 mt-4 rounded-xl border px-4 py-3 ${
            isDark ? 'bg-red-500/[0.08] border-red-500/30' : 'bg-red-50 border-red-200'
          }`} data-testid="pending-payment-banner">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center ${isDark ? 'bg-red-500/15' : 'bg-red-100'}`}>
                  <Receipt className={`w-4.5 h-4.5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${isDark ? 'text-red-300' : 'text-red-900'}`}>
                    Payment Required — {pendingPayment.plan_name || "Subscription"} Plan
                  </p>
                  <p className={`text-xs ${isDark ? 'text-red-400/80' : 'text-red-700'}`}>
                    ${pendingPayment.amount}/{pendingPayment.billing_interval === "year" ? "yr" : "mo"}
                    {pendingPayment.promo_code ? ` (promo: ${pendingPayment.promo_code})` : ""}
                    {" — "}Your admin has requested you complete this payment to continue using the service.
                  </p>
                </div>
              </div>
              <a href={pendingPayment.checkout_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 w-full sm:w-auto">
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold h-9 px-5 w-full sm:w-auto"
                  data-testid="pay-now-btn"
                >
                  Pay Now
                </Button>
              </a>
            </div>
          </div>
        )}

        {/* Upgrade Banner - Show for Owners without active subscription (never for team members) */}
        {user && !isMobileApp && !user.parent_user_id && user.subscription_plan === "starter" && user.subscription_status !== "active" && user.subscription_status !== "trialing" && (
          <div className={`mx-4 lg:mx-8 mt-4 rounded-xl border px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 ${
            isDark ? 'bg-[#FFCC00]/[0.06] border-[#FFCC00]/20' : 'bg-amber-50 border-amber-200'
          }`} data-testid="upgrade-banner">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center ${isDark ? 'bg-[#FFCC00]/15' : 'bg-amber-100'}`}>
                <Wallet className={`w-4.5 h-4.5 ${isDark ? 'text-[#FFCC00]' : 'text-amber-600'}`} />
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-medium ${isDark ? 'text-foreground' : 'text-amber-900'}`}>
                  You're on the Free plan
                </p>
                <p className={`text-xs ${isDark ? 'text-muted-foreground' : 'text-amber-700'}`}>
                  Upgrade to unlock unlimited jobs, SMS automations, and more
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/dashboard/settings?tab=my-plan')}
              className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90 text-xs font-semibold h-8 px-4 flex-shrink-0 w-full sm:w-auto"
              data-testid="upgrade-banner-btn"
            >
              View Plans
            </Button>
          </div>
        )}

        {/* Page Content */}
        <main className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="dashboard-content">
          {isOwnerAccount && location.pathname === "/dashboard" && (
            <SetupGuideWidget
              status={onbStatus}
              onOpen={() => setWizardOpen(true)}
              onRefresh={fetchOnboardingStatus}
            />
          )}
          <PageErrorBoundary>
            <Outlet />
          </PageErrorBoundary>
        </main>
      </div>

      {/* Guided Account Setup wizard (full-screen; below the plan modal z-index) */}
      <OnboardingWizard
        open={wizardOpen && !showPlanModal}
        onClose={() => { setWizardOpen(false); fetchOnboardingStatus(); }}
        initialStatus={onbStatus}
      />

      {/* Tap to Pay: warm-up, awareness moment & unresolved-outcome check (native app) */}
      <TapToPayBoot kind="owner" />

      {/* Mobile Bottom Navigation - Only visible on mobile */}
      {/* FAB Quick Actions Menu */}
      {fabOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="fab-menu">
          {/* Dimmed backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setFabOpen(false)}
            onTouchEnd={(e) => { e.preventDefault(); setFabOpen(false); }}
            style={{ touchAction: "manipulation" }}
          />
          {/* Action items - positioned above the FAB */}
          <div className="absolute bottom-24 left-0 right-0 flex flex-col items-center gap-2.5 px-6">
            {[
              { icon: UserPlus, label: "Client", color: "bg-blue-500", to: "/dashboard/customers" },
              { icon: Hammer, label: "Job", color: "bg-[#FFCC00]", textColor: "text-black", to: "/dashboard/jobs?action=new" },
              { icon: Receipt, label: "Invoice", color: "bg-emerald-500", to: "/dashboard/invoices" },
              { icon: Send, label: "Message", color: "bg-green-500", to: "/dashboard/messages" },
              { icon: Phone, label: "Call", color: "bg-purple-500", to: "/dashboard/calls" },
              { icon: FileText, label: "Estimate", color: "bg-orange-500", to: "/dashboard/invoices?type=estimate" },
            ].map((item, i) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setFabOpen(false)}
                className="flex items-center gap-3 w-full max-w-xs opacity-0"
                style={{ 
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  animation: `fabSlideUp 200ms ease-out ${i * 40}ms forwards`
                }}
                data-testid={`fab-action-${item.label.toLowerCase()}`}
              >
                <div className="flex-1 text-right">
                  <span className="inline-block px-4 py-2.5 rounded-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-semibold shadow-lg">
                    {item.label}
                  </span>
                </div>
                <div className={`w-11 h-11 rounded-full ${item.color} flex items-center justify-center shadow-lg shrink-0`}>
                  <item.icon className={`w-5 h-5 ${item.textColor || "text-white"}`} />
                </div>
              </Link>
            ))}
          </div>
          <style>{`
            @keyframes fabSlideUp {
              from { opacity: 0; transform: translateY(16px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

      <nav className="fixed bottom-0 left-0 right-0 lg:hidden z-50" data-testid="mobile-bottom-nav">
        <div className={`glass-card border-t backdrop-blur-xl ${isDark ? 'border-white/10 bg-[#0a0a12]/95' : 'border-gray-200 bg-white/95'}`}>
          <div className="flex items-center justify-around py-2 px-1">
            {/* Home */}
            <Link
              to="/dashboard"
              className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all ${
                location.pathname === "/dashboard"
                  ? "text-[#FFCC00] bg-[#FFCC00]/10"
                  : isDark ? "text-white/60 hover:text-white active:bg-white/5" : "text-gray-500 hover:text-gray-900 active:bg-gray-100"
              }`}
              data-testid="mobile-nav-home"
            >
              <Home className={`w-5 h-5 ${location.pathname === "/dashboard" ? "text-[#FFCC00]" : ""}`} />
              <span className="text-[10px] mt-1 font-medium">Home</span>
            </Link>

            {/* Jobs */}
            <Link
              to="/dashboard/jobs"
              className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all ${
                isActive("/dashboard/jobs")
                  ? "text-[#FFCC00] bg-[#FFCC00]/10"
                  : isDark ? "text-white/60 hover:text-white active:bg-white/5" : "text-gray-500 hover:text-gray-900 active:bg-gray-100"
              }`}
              data-testid="mobile-nav-jobs"
            >
              <Briefcase className={`w-5 h-5 ${isActive("/dashboard/jobs") ? "text-[#FFCC00]" : ""}`} />
              <span className="text-[10px] mt-1 font-medium">Jobs</span>
            </Link>

            {/* Create - Center FAB button */}
            <div
              role="button"
              tabIndex={0}
              onTouchEnd={(e) => { e.preventDefault(); setFabOpen(prev => !prev); }}
              onClick={() => setFabOpen(prev => !prev)}
              className="flex flex-col items-center justify-center -mt-6 cursor-pointer select-none"
              style={{ WebkitTapHighlightColor: "transparent", touchAction: "manipulation" }}
              data-testid="mobile-nav-create-job"
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
                fabOpen 
                  ? "bg-gray-800 dark:bg-white rotate-45 shadow-gray-800/30" 
                  : "bg-[#FFCC00] shadow-[#FFCC00]/30"
              }`}>
                <Plus className={`w-7 h-7 transition-colors ${fabOpen ? "text-white dark:text-black" : "text-black"}`} />
              </div>
              <span className={`text-[10px] mt-1 font-medium ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {fabOpen ? "Close" : "Create"}
              </span>
            </div>

            {/* Messages */}
            <Link
              to="/dashboard/messages"
              className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all relative ${
                isActive("/dashboard/messages")
                  ? "text-[#FFCC00] bg-[#FFCC00]/10"
                  : isDark ? "text-white/60 hover:text-white active:bg-white/5" : "text-gray-500 hover:text-gray-900 active:bg-gray-100"
              }`}
              data-testid="mobile-nav-messages"
            >
              <div className="relative">
                <MessageSquare className={`w-5 h-5 ${isActive("/dashboard/messages") ? "text-[#FFCC00]" : ""}`} />
                {unreadMessages > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-4 h-4 px-1 bg-[#FFCC00] text-black text-[10px] font-bold rounded-full">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </div>
              <span className="text-[10px] mt-1 font-medium">Messages</span>
            </Link>
            
            {/* More Button */}
            <button
              onClick={() => setMobileOpen(true)}
              className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all ${isDark ? 'text-white/60 hover:text-white active:bg-white/5' : 'text-gray-500 hover:text-gray-900 active:bg-gray-100'}`}
              data-testid="mobile-nav-more"
            >
              <LayoutGrid className="w-5 h-5" />
              <span className="text-[10px] mt-1 font-medium">More</span>
            </button>
          </div>
          
          {/* Safe area for devices with home indicator */}
          <div className={`h-safe-area-inset-bottom ${isDark ? 'bg-[#0a0a12]/95' : 'bg-white/95'}`} />
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
